import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Package, Truck, BarChart3, Settings, Zap, Users, CheckCircle, Radio } from 'lucide-react';

const WarehouseManagementPage = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const features = [
    {
      title: 'RFID-Based Inventory Tracking',
      description: 'Advanced radio frequency identification systems for real-time inventory tracking and management. Our RFID solutions provide accurate, automated tracking of goods throughout the warehouse lifecycle, eliminating manual scanning errors and improving inventory accuracy.',
      icon: Radio,
      image: 'https://images.pexels.com/photos/5025643/pexels-photo-5025643.jpeg',
    },
    {
      title: 'Automated Forklift Operations',
      description: 'Smart forklift management systems with GPS tracking, automated routing, and predictive maintenance. Our solutions optimize forklift utilization, reduce operational costs, and enhance safety through intelligent automation and real-time monitoring.',
      icon: Truck,
      image: 'https://images.pexels.com/photos/4487418/pexels-photo-4487418.jpeg',
    },
    {
      title: 'Warehouse Automation Systems',
      description: 'Comprehensive warehouse automation including conveyor systems, automated storage and retrieval systems (AS/RS), and robotic picking solutions. Our automation systems increase throughput, reduce labor costs, and improve operational efficiency.',
      icon: Settings,
      image: 'https://images.unsplash.com/photo-1592085198739-ffcad7f36b54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHx3YXJlaG91c2UlMjBhdXRvbWF0aW9ufGVufDB8fHx8MTc1MjI0ODI0MXww&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Intelligent Inventory Management',
      description: 'AI-powered inventory optimization with predictive analytics, demand forecasting, and automated reordering systems. Our intelligent systems reduce stockouts, minimize carrying costs, and optimize inventory levels based on historical data and market trends.',
      icon: BarChart3,
      image: 'https://images.unsplash.com/photo-1689942010216-dc412bb1e7a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBhdXRvbWF0aW9ufGVufDB8fHx8MTc1MjI0ODI0MXww&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Real-Time Logistics Optimization',
      description: 'Dynamic routing and scheduling systems that optimize warehouse operations in real-time. Our solutions coordinate inbound and outbound logistics, reduce dwell times, and improve overall warehouse throughput through intelligent scheduling and resource allocation.',
      icon: Zap,
      image: 'https://images.pexels.com/photos/574071/pexels-photo-574071.jpeg',
    },
    {
      title: 'Workforce Management Solutions',
      description: 'Comprehensive workforce management systems including task assignment, performance tracking, and safety monitoring. Our solutions optimize labor allocation, improve productivity, and ensure compliance with safety protocols in warehouse environments.',
      icon: Users,
      image: 'https://images.pexels.com/photos/5025643/pexels-photo-5025643.jpeg',
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1689942010216-dc412bb1e7a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBhdXRvbWF0aW9ufGVufDB8fHx8MTc1MjI0ODI0MXww&ixlib=rb-4.1.0&q=85')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-r from-orange-900/90 to-near-black/90 z-0" />
        <div className="absolute inset-0 grid-pattern opacity-20 z-1" />
        <div className="container relative z-10">
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-700 rounded-16 flex items-center justify-center">
                <Package className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="heading-font text-4xl md:text-6xl font-bold text-off-white">
                  Warehouse <span className="text-royal-gold">Management</span>
                </h1>
              </div>
            </div>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              Advanced RFID-based inventory tracking, automated forklift operations, and intelligent warehouse management solutions. Our comprehensive systems optimize supply chain operations, reduce costs, and enhance security for government and enterprise logistics facilities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-gray-dark" ref={ref}>
        <div className="container">
          <div className="space-y-20">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-700 rounded-16 flex items-center justify-center">
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="heading-font text-3xl md:text-4xl font-bold text-off-white">
                        {feature.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-lg text-gray-300 mb-8 leading-relaxed">{feature.description}</p>
                </div>
                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative rounded-24 overflow-hidden group">
                    <img src={feature.image} alt={feature.title} className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-near-black/60 to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-700 rounded-12 flex items-center justify-center">
                        <feature.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WarehouseManagementPage;